import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  FloatingPortal,
  offset,
  Placement,
  shift,
  size,
  useFloating,
  type FloatingContext,
  type ReferenceType,
  type Strategy,
} from "@floating-ui/react";
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useMemo,
  useState,
  type RefCallback,
} from "react";
import { twMerge } from "tailwind-merge";
import { Icon, IconType } from "@sellar-io/components";
import { useFloatingLayer } from "@sellar-io/components";

type TooltipDismissContextValue = {
  x: number | null;
  y: number | null;
  strategy: Strategy;
  setFloating:
    | RefCallback<HTMLElement | null>
    | ((node: HTMLElement | null) => void);
  setArrowElement: Dispatch<SetStateAction<SVGSVGElement | null>>;
  context: FloatingContext<ReferenceType>;
};

const TooltipDismissContext = createContext<TooltipDismissContextValue | null>(
  null,
);

const TooltipDismissRoot = ({
  children,
  placement = "bottom",
}: {
  children: ReactNode;
  placement?: Placement;
}) => {
  const [arrowElement, setArrowElement] = useState<SVGSVGElement | null>(null);
  const floating = useFloating({
    placement,
    strategy: "fixed",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(12),
      flip(),
      shift({ padding: 8 }),
      size({
        padding: 8,
        apply({ availableWidth, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            maxWidth: `${availableWidth}px`,
            maxHeight: `${availableHeight}px`,
          });
        },
      }),
      arrow({ element: arrowElement, padding: 8 }),
    ],
  });

  // Destructure ref setters so we never read `.refs` during render in JSX.
  const { setReference, setFloating } = floating.refs;

  const contextValue = useMemo(
    () => ({
      x: floating.x,
      y: floating.y,
      strategy: floating.strategy,
      setFloating,
      setArrowElement,
      context: floating.context,
    }),
    [floating.x, floating.y, floating.strategy, floating.context, setFloating],
  );

  return (
    <TooltipDismissContext.Provider value={contextValue}>
      <div ref={setReference} className="relative w-full">
        {children}
      </div>
    </TooltipDismissContext.Provider>
  );
};

const Content = ({
  icon,
  iconSize = 0.85,
  title,
  description,
  className,
  showAnchor = true,
  onClick,
}: {
  icon?: IconType;
  iconSize?: number;
  title: string;
  description: ReactNode;
  className?: string;
  showAnchor?: boolean;
  onClick?: () => void;
}) => {
  const context = useContext(TooltipDismissContext);
  const { tooltip } = useFloatingLayer();

  if (!context) {
    throw new Error("TooltipDismissContent must be used within TooltipDismiss");
  }

  const { x, y, strategy, setFloating, setArrowElement } = context;

  return (
    <FloatingPortal>
      <div
        ref={setFloating}
        className={twMerge(
          `${tooltip} w-[300px] rounded-lg border border-gray-300 bg-white text-sm shadow-lg`,
          className,
        )}
        style={{
          position: strategy,
          top: y ?? 0,
          left: x ?? 0,
          visibility: x == null ? "hidden" : "visible",
        }}
      >
        <div
          className="overflow-auto rounded-[inherit] px-4 py-3"
          style={{ maxHeight: "inherit" }}
        >
          <div className="flex items-center gap-2 bg-white">
            {icon && (
              <Icon
                icon="INFORMATION_OUTLINE"
                size={iconSize}
                className="text-zinc-800"
              />
            )}
            <div className="min-w-0 flex-1">
              <div className="flex flex-row items-center justify-between">
                <div className="text-sm font-semibold text-zinc-800">
                  {title}
                </div>
                <Icon
                  className="cursor-pointer"
                  icon="CLOSE"
                  onClick={onClick}
                />
              </div>
              <div className="text-xs text-zinc-700">{description}</div>
            </div>
          </div>
        </div>
        {showAnchor && (
          <FloatingArrow
            ref={setArrowElement}
            context={context.context}
            width={16}
            height={8}
            fill="white"
            stroke="#d1d5db"
            strokeWidth={1}
          />
        )}
      </div>
    </FloatingPortal>
  );
};

export {
  TooltipDismissRoot as TooltipDismiss,
  Content as TooltipDismissContent,
};
